import { useCallback, useEffect, useRef, useState } from "react"

import { shareInFlight } from "../../../components/Esign/signing/in-flight.ts"
import type { Outcome } from "../../../components/Esign/signing/types.ts"
import { useNewestOnly } from "../../../components/Esign/signing/use-newest-only.ts"
import { ask, isGone } from "../../../components/Esign/signing/use-signing.ts"
import { getPermissionValueAtPath } from "../../../components/PermissionGate/PermissionGate.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { getCountersignState } from "../api/countersign.services.ts"
import type { CountersignState } from "../types/countersign.types.ts"

export const COUNTERSIGN_PERMISSION =
  "esign.signatures.document_countersign" as const

export const useCanCountersign = () =>
  useAppSelector((state) =>
    getPermissionValueAtPath(
      state.auth?.permissions?.codes,
      COUNTERSIGN_PERMISSION
    )
  )

export type CountersignLoad =
  | { kind: "loading" }
  | { kind: "ready"; state: CountersignState }
  // The page does not say which envelopes exist, so hidden and missing read the same.
  | { kind: "gone" }
  | { kind: "failed"; outcome: Outcome<unknown> }

/** Until the colleague consents every read is an audited disclosure view, so concurrent reads share one. */
export const useCountersignState = (id: string) => {
  const [load, setLoad] = useState<CountersignLoad>({ kind: "loading" })
  const shared = useRef<{
    id: string
    read: () => Promise<Outcome<CountersignState>>
  } | null>(null)
  const newest = useNewestOnly()

  const read = useCallback(() => {
    if (shared.current?.id !== id) {
      shared.current = {
        id,
        read: shareInFlight(() => ask(() => getCountersignState(id)))
      }
    }
    return shared.current.read()
  }, [id])

  const apply = useCallback((outcome: Outcome<CountersignState>) => {
    if (outcome.ok && outcome.data)
      setLoad({ kind: "ready", state: outcome.data })
    else if (isGone(outcome)) setLoad({ kind: "gone" })
    else setLoad({ kind: "failed", outcome })
  }, [])

  const start = useCallback(() => {
    const current = newest.claim()
    setLoad({ kind: "loading" })
    void read().then((outcome) => {
      if (current()) apply(outcome)
    })
  }, [read, apply, newest])

  useEffect(start, [start])

  const reload = useCallback(async () => {
    const current = newest.claim()
    const outcome = await read()
    if (current()) apply(outcome)
    return outcome
  }, [read, apply, newest])

  return { load, reload, retry: start }
}
